/**
 * Renderização determinística: bytes derivados → senha.
 *
 * A chave de 128 bytes é lida como um único inteiro grande e consumida por
 * divisões sucessivas. Isso é determinístico, independente de plataforma e
 * garante a conformidade com a política (ao menos um caractere de cada classe
 * exigida) sem precisar de tentativa-e-erro.
 */

import {
  AMBIGUOUS,
  DIGITS,
  LOWER,
  SYMBOLS_EXTENDED,
  SYMBOLS_SAFE,
  UPPER,
  type Policy,
} from './spec'
import { assertValidPolicy } from './salt'

const AMBIGUOUS_SET = new Set(AMBIGUOUS.split(''))

function strip(charset: string, excludeAmbiguous: boolean): string {
  if (!excludeAmbiguous) return charset
  return charset
    .split('')
    .filter((c) => !AMBIGUOUS_SET.has(c))
    .join('')
}

/**
 * Conjuntos habilitados, em ordem fixa. A ordem faz parte do algoritmo:
 * trocá-la mudaria todas as senhas.
 */
export function charsetsFor(policy: Policy): { full: string; required: string[] } {
  assertValidPolicy(policy)
  const symbols = policy.symbolSet === 'extended' ? SYMBOLS_EXTENDED : SYMBOLS_SAFE
  const required: string[] = []
  if (policy.lower) required.push(strip(LOWER, policy.excludeAmbiguous))
  if (policy.upper) required.push(strip(UPPER, policy.excludeAmbiguous))
  if (policy.digits) required.push(strip(DIGITS, policy.excludeAmbiguous))
  if (policy.symbols) required.push(strip(symbols, policy.excludeAmbiguous))

  for (const set of required) {
    if (set.length === 0) {
      throw new RangeError(
        'a exclusão de caracteres ambíguos esvaziou um dos conjuntos selecionados',
      )
    }
  }
  if (policy.length < required.length) {
    throw new RangeError(
      `comprimento ${policy.length} é curto demais para ${required.length} classes obrigatórias`,
    )
  }
  return { full: required.join(''), required }
}

/** Interpreta os bytes como um inteiro big-endian sem sinal. */
export function bytesToBigInt(bytes: Uint8Array): bigint {
  let n = 0n
  for (const byte of bytes) n = (n << 8n) | BigInt(byte)
  return n
}

/** Consome um índice do inteiro e devolve o resto para a próxima extração. */
function take(n: bigint, modulus: number): [bigint, number] {
  const m = BigInt(modulus)
  return [n / m, Number(n % m)]
}

/**
 * Consome os bytes derivados e produz a senha.
 *
 * O inteiro tem 1024 bits e no pior caso (64 caracteres sobre 90 símbolos)
 * consumimos ~450 bits, então o viés de módulo é astronomicamente pequeno.
 */
export function renderPassword(key: Uint8Array, policy: Policy): string {
  const { full, required } = charsetsFor(policy)
  let n = bytesToBigInt(key)

  const chars: string[] = []
  const bulk = policy.length - required.length
  for (let i = 0; i < bulk; i += 1) {
    const [rest, idx] = take(n, full.length)
    n = rest
    chars.push(full[idx]!)
  }

  // Um caractere garantido de cada classe exigida...
  const mandatory: string[] = []
  for (const set of required) {
    const [rest, idx] = take(n, set.length)
    n = rest
    mandatory.push(set[idx]!)
  }

  // ...inserido numa posição também derivada dos bytes, para não ficar sempre no fim.
  for (const c of mandatory) {
    const [rest, pos] = take(n, chars.length + 1)
    n = rest
    chars.splice(pos, 0, c)
  }

  return chars.join('')
}

/** Entropia teórica da senha renderizada, em bits. Usada só como indicador na UI. */
export function entropyBits(policy: Policy): number {
  const { full } = charsetsFor(policy)
  return policy.length * Math.log2(full.length)
}

/** Verifica que a senha respeita a política — usado nos testes e como sanidade em runtime. */
export function satisfiesPolicy(password: string, policy: Policy): boolean {
  if (password.length !== policy.length) return false
  const { full, required } = charsetsFor(policy)
  const allowed = new Set(full.split(''))
  for (const c of password) if (!allowed.has(c)) return false
  for (const set of required) {
    const members = new Set(set.split(''))
    if (![...password].some((c) => members.has(c))) return false
  }
  return true
}
